import React from "react";
import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";
import type { Slot, PollInsights } from "../../lib/insights/types";
import { formatDate, formatTime } from "../voting/utils/dateUtils";

interface WinnerBannerProps {
  slots: Slot[];
  insights: PollInsights;
  onFinalize?: (slotId: string) => void;
}

/**
 * Bandeau "Créneau en tête" — toujours visible au-dessus de la table.
 * Cf. Results Final.html lignes 147-165.
 */
const WinnerBanner: React.FC<WinnerBannerProps> = ({
  slots,
  insights,
  onFinalize,
}) => {
  const { t } = useTranslation("polls");
  const winnerId = insights.winnerSlotId;
  if (!winnerId) return null;
  const winner = slots.find((s) => s.id === winnerId);
  const score = insights.scores.find((s) => s.slotId === winnerId);
  if (!winner || !score) return null;

  const total = insights.participantsCount;
  const dayLabel = winner.groupLabel ?? formatDate(winner.primaryDate);
  const timeLabel =
    winner.timeSlots && winner.timeSlots.length > 0
      ? formatTime(winner.timeSlots)
      : null;

  return (
    <div
      data-testid="winner-banner"
      className="flex items-center gap-3 rounded-xl border border-success/40 bg-success/10 px-4 py-3"
    >
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-success text-success-foreground">
        <Star className="h-4 w-4 fill-current" aria-hidden />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t("results_leading_slot")}
        </div>
        <div className="text-sm font-semibold text-foreground truncate">
          {dayLabel}
          {timeLabel && (
            <span className="text-muted-foreground"> · {timeLabel}</span>
          )}
          <span className="ml-3 font-medium text-success tabular-nums">
            {score.yes}/{total} {t("results_yes_label")}
          </span>
        </div>
      </div>
      {onFinalize && (
        <button
          type="button"
          data-testid="winner-finalize"
          onClick={() => onFinalize(winnerId)}
          className="h-9 rounded-md bg-success px-4 text-[13px] font-semibold text-success-foreground hover:bg-success/90 transition-colors"
        >
          {t("results_set_this_date")}
        </button>
      )}
    </div>
  );
};

export default WinnerBanner;
